/**
 * Staff — Training welcome email templates.
 *
 * Per-brand (ProBound / ProEpoxy / ProDecor) editable subject + HTML body with
 * placeholders like {{trainee_name}}, {{course_date}}, {{balance_owed}}. A
 * preview panel renders the template against sample context so staff can see
 * exactly what the trainee will receive.
 */
import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Home, Mail, Save, Eye, RotateCcw, Send } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { formatApiError } from '@/utils/apiError';

const BRANDS = [
  { key: 'probound', label: 'ProBound',  accent: '#2F5E96' },
  { key: 'proepoxy', label: 'ProEpoxy',  accent: '#7A0B7A' },
  { key: 'prodecor', label: 'ProDecor',  accent: '#D5AB09' },
];

export default function TrainingEmailTemplates() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [active, setActive] = useState('probound');
  const [templates, setTemplates] = useState({});   // { brand: {subject, body_html} }
  const [dirty, setDirty] = useState({});           // { brand: true }
  const [variables, setVariables] = useState([]);
  const [preview, setPreview] = useState(null);     // { subject, body_html } — always kept up to date
  const [previewing, setPreviewing] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const previewTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rList, rVars] = await Promise.all([
        fetch(`${API_URL}/api/staff/training-email-templates`, { credentials: 'include' }),
        fetch(`${API_URL}/api/staff/training-email-templates/variables`, { credentials: 'include' }),
      ]);
      if (!rList.ok || !rVars.ok) throw new Error('Failed to load templates');
      const list = await rList.json();
      const vars_ = (await rVars.json()).variables || [];
      const byBrand = {};
      for (const t of list) byBrand[t.brand] = { subject: t.subject, body_html: t.body_html };
      setTemplates(byBrand);
      setVariables(vars_);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [user, navigate, load]);

  const setField = (brand, field, val) => {
    setTemplates((t) => ({ ...t, [brand]: { ...t[brand], [field]: val } }));
    setDirty((d) => ({ ...d, [brand]: true }));
  };

  const insertVariable = (brand, placeholder) => {
    // Append into the body_html at the end — simple but useful.
    setTemplates((t) => ({
      ...t,
      [brand]: { ...t[brand], body_html: `${t[brand]?.body_html || ''}${placeholder}` },
    }));
    setDirty((d) => ({ ...d, [brand]: true }));
  };

  const save = async (brand) => {
    setSaving(true);
    try {
      const tpl = templates[brand];
      const res = await fetch(`${API_URL}/api/staff/training-email-templates/${brand}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: tpl.subject, body_html: tpl.body_html }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Save failed');
      }
      toast.success(`${brand.toUpperCase()} template saved`);
      setDirty((d) => ({ ...d, [brand]: false }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // showPreview reads the freshest templates via a ref so it can stay a
  // stable useCallback — otherwise it would re-create on every keystroke
  // and defeat the 400ms debounce below.
  const templatesRef = useRef(templates);
  useEffect(() => { templatesRef.current = templates; }, [templates]);

  const showPreview = useCallback(async (brand, { silent = false } = {}) => {
    if (!silent) setPreviewing(true);
    try {
      const tpl = templatesRef.current[brand];
      if (!tpl?.subject || !tpl?.body_html) return;
      const res = await fetch(`${API_URL}/api/staff/training-email-templates/preview`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: tpl.subject, body_html: tpl.body_html }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Preview failed');
      }
      setPreview(await res.json());
    } catch (e) {
      if (!silent) toast.error(e.message);
    } finally {
      if (!silent) setPreviewing(false);
    }
  }, []);

  // Live preview — refresh 400ms after the staff stops typing on the active
  // template. Keeps the right-side panel in sync without hammering the server.
  const activeSubject = templates[active]?.subject;
  const activeBody = templates[active]?.body_html;
  useEffect(() => {
    if (!activeSubject || !activeBody) return undefined;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      showPreview(active, { silent: true });
    }, 400);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [active, activeSubject, activeBody, showPreview]);

  const sendTestToMe = async (brand) => {
    setSendingTest(true);
    try {
      const tpl = templates[brand];
      const res = await fetch(`${API_URL}/api/staff/training-email-templates/send-test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: tpl.subject, body_html: tpl.body_html }),
      });
      if (!res.ok) {
        // Some edges (Cloudflare) rewrite non-2xx bodies to HTML "Bad gateway"
        // pages, which obliterates our FastAPI JSON detail. If json() fails
        // we fall back to status + statusText so staff still see a hint.
        const err = await res.json().catch(() => null);
        const msg = formatApiError(err) || `Test send failed (${res.status} ${res.statusText || ''})`.trim();
        throw new Error(msg);
      }
      const data = await res.json();
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Loading templates…</div>;
  }

  const current = templates[active] || { subject: '', body_html: '' };
  const brand = BRANDS.find((b) => b.key === active);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b sticky top-0 z-20 bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/staff/training-courses" className="text-gray-500 hover:text-gray-900" data-testid="templates-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" className="text-gray-500 hover:text-gray-900">
            <Home className="w-5 h-5" />
          </Link>
          <Mail className="w-5 h-5 text-gray-500" />
          <h1 className="font-bold text-gray-900">Training welcome emails</h1>
          <div className="ml-auto text-xs text-gray-500">Per-brand · edits apply to every new registration</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Brand tabs */}
        <div className="flex gap-2 flex-wrap">
          {BRANDS.map((b) => (
            <button
              key={b.key}
              onClick={() => setActive(b.key)}
              data-testid={`tab-${b.key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                active === b.key ? 'text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
              style={{ background: active === b.key ? b.accent : undefined }}
            >
              {b.label}
              {dirty[b.key] && <span className="ml-1 text-xs">•</span>}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
          <Card className="p-5 space-y-4" style={{ borderTop: `4px solid ${brand.accent}` }}>
            <div>
              <Label htmlFor="tpl-subject" className="text-sm font-semibold">Subject line</Label>
              <Input
                id="tpl-subject"
                value={current.subject}
                onChange={(e) => setField(active, 'subject', e.target.value)}
                data-testid="template-subject"
                placeholder="e.g. Your ProBound training — joining instructions"
                className="mt-1 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="tpl-body" className="text-sm font-semibold">Body (HTML)</Label>
              <Textarea
                id="tpl-body"
                rows={22}
                value={current.body_html}
                onChange={(e) => setField(active, 'body_html', e.target.value)}
                data-testid="template-body"
                className="mt-1 font-mono text-xs bg-white"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Use HTML for formatting. Insert variables like <code>{'{{'}trainee_name{'}}'}</code> or <code>{'{{'}venue_address{'}}'}</code> / <code>{'{{'}venue_map_link{'}}'}</code> (from Training Fees → Training venue) — they&apos;re replaced when the email is sent.
              </p>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Available variables</h3>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <button
                    key={v}
                    onClick={() => insertVariable(active, v)}
                    className="text-[11px] font-mono px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
                    data-testid={`var-${v.replace(/[{}]/g, '')}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button
                onClick={() => save(active)}
                disabled={saving || !dirty[active]}
                className="text-white font-bold"
                style={{ background: brand.accent }}
                data-testid="template-save"
              >
                <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                variant="outline"
                onClick={() => sendTestToMe(active)}
                disabled={sendingTest || !current.subject || !current.body_html}
                data-testid="template-send-to-me"
              >
                <Send className={`w-4 h-4 mr-1.5 ${sendingTest ? 'animate-pulse' : ''}`} />
                {sendingTest ? 'Sending…' : (user?.email ? `Send to ${user.email}` : 'Send to me')}
              </Button>
              <Button variant="outline" onClick={() => showPreview(active)} data-testid="template-preview" disabled={previewing}>
                <Eye className="w-4 h-4 mr-1.5" /> Refresh preview
              </Button>
              <Button variant="outline" onClick={load} data-testid="template-reload">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Reload
              </Button>
            </div>
          </Card>

          {/* Live preview panel — always visible, auto-refreshes 400ms after edits. */}
          <Card className="p-0 overflow-hidden lg:sticky lg:top-20 self-start" data-testid="template-live-preview">
            <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Eye className="w-4 h-4 text-gray-500 shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-700 truncate">Live preview</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {preview?.subject || <span className="italic">(subject will render as you type…)</span>}
                  </div>
                </div>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 shrink-0">
                Sample data
              </span>
            </div>
            <div className="bg-white overflow-auto" style={{ maxHeight: 720 }}>
              {preview ? (
                <div className="p-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.body_html, { ADD_ATTR: ['target'] }) }} data-testid="template-preview-body" />
              ) : (
                <div className="p-8 text-center text-sm text-gray-400 italic">
                  Type into the editor and the preview will render here automatically.
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
